import { useCallback, useEffect, useRef, useState } from "react";

export interface TimerHandle {
  /** milliseconds left, updated ~5×/second */
  remaining: number;
  /** seconds the current run was started with */
  total: number;
  running: boolean;
  start: (secs: number) => void;
  resume: () => void;
  pause: () => void;
  stop: () => void;
  /** epoch ms the current run is due to end — used to schedule the rest-over notification */
  endsAt: () => number;
}

/**
 * Countdown driven by wall-clock timestamps rather than by accumulating ticks, so the
 * remaining time stays correct even when the OS suspends the tab mid-session.
 *
 * `onSecond` fires once per whole second (for the 3-2-1 beeps); `onEnd` fires once at zero.
 */
export function useTimer(
  onSecond?: (secondsLeft: number) => void,
  onEnd?: () => void
): TimerHandle {
  const endRef = useRef(0);
  const leftRef = useRef(0);
  const totalRef = useRef(0);
  const lastSecRef = useRef(-1);
  const ivRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endedRef = useRef(false);

  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [total, setTotal] = useState(0);

  const cbSecond = useRef(onSecond);
  const cbEnd = useRef(onEnd);
  cbSecond.current = onSecond;
  cbEnd.current = onEnd;

  const clear = useCallback(() => {
    if (ivRef.current) clearInterval(ivRef.current);
    ivRef.current = null;
  }, []);

  const tick = useCallback(() => {
    const r = Math.max(0, endRef.current - Date.now());
    setRemaining(r);

    const s = Math.ceil(r / 1000);
    if (s !== lastSecRef.current) {
      lastSecRef.current = s;
      cbSecond.current?.(s);
    }

    if (r <= 0 && !endedRef.current) {
      endedRef.current = true;
      clear();
      setRunning(false);
      cbEnd.current?.();
    }
  }, [clear]);

  const resume = useCallback(() => {
    if (ivRef.current) return;
    endedRef.current = false;
    endRef.current = Date.now() + leftRef.current;
    setRunning(true);
    ivRef.current = setInterval(tick, 200);
    tick();
  }, [tick]);

  const start = useCallback(
    (secs: number) => {
      clear();
      totalRef.current = secs;
      setTotal(secs);
      leftRef.current = secs * 1000;
      lastSecRef.current = -1;
      endedRef.current = false;
      resume();
    },
    [clear, resume]
  );

  const pause = useCallback(() => {
    if (!ivRef.current) return;
    leftRef.current = Math.max(0, endRef.current - Date.now());
    clear();
    setRunning(false);
  }, [clear]);

  const stop = useCallback(() => {
    clear();
    leftRef.current = 0;
    endedRef.current = true;
    setRemaining(0);
    setRunning(false);
  }, [clear]);

  const endsAt = useCallback(() => endRef.current, []);

  // a backgrounded tab throttles setInterval; re-read the clock the moment it comes back
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && ivRef.current) tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [tick]);

  useEffect(() => clear, [clear]);

  return { remaining, total, running, start, resume, pause, stop, endsAt };
}
